import React, { useEffect } from 'react';
import { AddressValidation } from '@js/formSettings/validations';
import { ErrorMessage } from '@hookform/error-message';
import { usePostalJp } from 'use-postal-jp';
import { SingleOwnerFormData } from '@js/interfaces/index';
import { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { prefectureOptions } from '@js/components/common/prefectures';

type Props = {
  register: UseFormRegister<{owner: SingleOwnerFormData[]}>
  watchPostalCode: string
  errors: FieldErrors<{owner: SingleOwnerFormData[]}>
  setValue: UseFormSetValue<{owner: SingleOwnerFormData[]}>
  index: number
  single_owner: SingleOwnerFormData
};

export const Address = (props: Props) => {
  const {
    register,
    setValue,
    watchPostalCode,
    errors,
    index,
    single_owner
  } = props;
  const [ address ] = usePostalJp(watchPostalCode, `${watchPostalCode}`.length > 6);

  useEffect(() => {
    if(address) {
      const prefecture = prefectureOptions.filter(prefecture => Number(prefecture.code) === Number(address.prefectureCode));
      if(prefecture.length < 1) return; //空配列が渡ってくるとエラーになってフォームが表示されなくなるため
      setValue(`owner.${index}.prefecture_name`, prefecture[0].name || '選択して下さい');
      setValue(`owner.${index}.city`, address.address1 + address.address2 + address.address3);
    }
  }, [address, index, setValue]);  
  const _errors = errors.owner && errors.owner[index];
  return (
    <>
      <div className="form-input__wrap">
        <label className="form-label" htmlFor={`owner.${index}.postal_code`}>
          <span className="form-label__required">必須</span>郵便番号
          <a className="form-label__link ml16" target="_blank" href="https://www.post.japanpost.jp/zipcode/index.html" rel="noreferrer">
            <i className="material-icons form-label__link-icon">search</i>
            郵便番号を調べる
          </a>
        </label>
        <div className="form-column">
          <div className="form-column__item">
            <input
              {...register(`owner.${index}.postal_code`, AddressValidation.postalCode)}
              onWheel={(e) => e.currentTarget.blur()}
              className={`form-input ${_errors && _errors.postal_code && 'is-error'}`}
              placeholder="例）1234567"
              type="number"
              id={`owner.${index}.postal_code`}
              defaultValue={single_owner.postal_code}
            />
          </div>
        </div>
        <ErrorMessage
          errors={errors}
          name={`owner.${index}.postal_code`}
          render={({ message }) =>
            <span className="form-input__error">郵便番号は{message}</span>}
        />
        <span className="form-input__note">※ハイフン無しでご入力ください</span>
      </div>
      <div className="form-input__wrap">
        <label className="form-label" htmlFor={`owner.${index}.prefecture_name`}>
          <span className="form-label__required">必須</span>都道府県
        </label>
        <div className="form-column">
          <div className="form-column__item">
            <div className="form-select__wrap">
              <select
                {...register(`owner.${index}.prefecture_name`, AddressValidation.prefectureName)}
                className={`form-select ${_errors && _errors.prefecture_name && 'is-error'}`}
                id={`owner.${index}.prefecture_name`}
                defaultValue={single_owner.prefecture_name}
              >
                {prefectureOptions.map((prefecture) => {
                  return <option value={prefecture.name} key={prefecture.code} >{prefecture.name}</option>;
                })}
              </select>
            </div>
          </div>
        </div>
        <ErrorMessage
          errors={errors}
          name={`owner.${index}.prefecture_name`}
          render={({ message }) =>
            <span className="form-input__error">都道府県は{message}</span>}
        />
      </div>
      <div className="form-input__wrap">
        <label className="form-label" htmlFor={`owner.${index}.city`}>
          <span className="form-label__required">必須</span>市区町村
        </label>
        <input
          {...register(`owner.${index}.city`, AddressValidation.city)}
          type="text"
          id={`owner.${index}.city`}
          className={`form-input ${_errors && _errors.city && 'is-error'}`}
          placeholder="例）渋谷区恵比寿南"
          defaultValue={single_owner.city}
        />
        <ErrorMessage
          errors={errors}
          name={`owner.${index}.city`}
          render={({ message }) =>
            <span className="form-input__error">市区町村は{message}</span>}
        />
      </div>
      <div className="form-input__wrap">
        <label className="form-label" htmlFor={`owner.${index}.street`}>
          <span className="form-label__required">必須</span>番地
        </label>
        <input
          {...register(`owner.${index}.street`, AddressValidation.street)}
          type="text"
          id={`owner.${index}.street`}
          className={`form-input ${_errors && _errors.street && 'is-error'}`}
          placeholder="例）1-2-3"
          defaultValue={single_owner.street}
        />
        <ErrorMessage
          errors={errors}
          name={`owner.${index}.street`}
          render={({ message }) =>
            <span className="form-input__error">番地は{message}</span>}
        />
      </div>
      <div className="form-input__wrap">
        <label className="form-label" htmlFor={`owner.${index}.building`}>
          <span className="form-label__conditional-required">該当する方は必須</span>建物名、部屋番号など
        </label>
        <input
          {...register(`owner.${index}.building`, AddressValidation.building)}
          type="text"
          id={`owner.${index}.building`}
          className={`form-input ${_errors && _errors.building && 'is-error'}`}
          placeholder="例）恵比寿ビルディング　505号室"
          defaultValue={single_owner.building}
        />
        <ErrorMessage
          errors={errors}
          name={`owner.${index}.building`}
          render={({ message }) =>
            <span className="form-input__error">建物名は{message}</span>}
        />
        <span className="form-input__note">※建物名や部屋番号がある場合は必ずご入力ください</span>
      </div>
    </>
  );
};
